import { UsersRound } from "lucide-react";
import { useLocation } from "wouter";
import { CardFacepile } from "@/components/threads/CardFacepile.tsx";
import { getThreadParticipantsPath } from "@/components/threads/util.ts";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu.tsx";
import type { GetThreadResp } from "@/types/thread.ts";

/**
 * The thread's participants plus the single thing every surface does with them,
 * so no two renderings can disagree about who is on the thread or where
 * clicking leads. All of them name the same canonical URL and let the nav
 * boundary choose the surface — the canvas on a wide window, a page on mobile.
 */
export const useThreadContacts = (thread: GetThreadResp) => {
  const [, setLocation] = useLocation();
  const contactsHref = getThreadParticipantsPath(thread.id);
  return {
    participants: thread.card?.participants ?? [],
    additional: thread.card?.additionalParticipantCount ?? 0,
    contactsHref,
    // For the menu row, which owns its own click and can't carry an anchor.
    openContacts: () => setLocation(contactsHref),
  };
};

/**
 * The facepile's home wherever it will not fit beside the thread — the detail
 * header once it is too narrow to carry it, and the list row below `md`, where
 * the faces would crowd a 44px line already holding a badge and a title.
 *
 * A menu row naming the action on the left with the faces trailing on the
 * right, so the participants stay glanceable rather than collapsing to a word.
 *
 * The facepile is passed no `onClick` deliberately: the row is already the
 * click target, and a nested button would strand keyboard users on a control
 * inside a control.
 */
export const ThreadContactsMenuItem = ({
  thread,
  statsigName,
}: {
  thread: GetThreadResp;
  /** Named per surface: which surface drove people to the participant list is
   *  the question this row exists to answer. */
  statsigName: string;
}) => {
  const { participants, additional, openContacts } = useThreadContacts(thread);
  return (
    // No gap of its own: the base item's `gap-2` is what sets every other row's
    // distance from its icon to its label, and this row reads as an outlier at
    // anything else.
    <DropdownMenuItem onClick={openContacts} data-statsig-name={statsigName}>
      <UsersRound className="size-4" />
      People
      {/* `ml-auto` rather than `justify-between` on the row, so the label starts
          where every other row's label does instead of being pushed by the
          faces.

          `-my-0.5` keeps the faces from setting the row's height. With a `+N`
          count the facepile wears a chip — a 20px avatar inside its own 2px of
          padding — and 24px of content in a row whose text is 20px made this
          the one item standing taller than the rest. The negative margin gives
          those 4px back to the item's own padding, which has room for them. */}
      <CardFacepile
        participants={participants}
        additional={additional}
        className="-my-0.5 ml-auto"
      />
    </DropdownMenuItem>
  );
};
